// ============================================================
// SmartCoachModal.jsx — props-based, paywall, 3-phase
// ============================================================
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getStepProgress } from './aiCoachEngine';
import './SmartCoachModal.css';

const toFa = (n) => String(n ?? '').replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
const safeArray = (v) => (Array.isArray(v) ? v : []);
const readJSON = (key, fb) => {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fb)); }
  catch { return fb; }
};

const MISSION_META = {
  resume_draft: { emoji: '⏰', color: '#ef4444', bg: '#ffe4e6', label: 'ادامه آزمون' },
  review_mistakes: { emoji: '🔍', color: '#f59e0b', bg: '#fef3c7', label: 'تحلیل خطا' },
  study: { emoji: '📚', color: '#8b5cf6', bg: '#f3e8ff', label: 'مطالعه' },
  review: { emoji: '🔄', color: '#8b5cf6', bg: '#f3e8ff', label: 'مرور' },
  finish_lesson: { emoji: '✅', color: '#22c55e', bg: '#d1fae5', label: 'تکمیل درس' },
  watch_video: { emoji: '🎬', color: '#ef4444', bg: '#fee2e2', label: 'ویدیو' },
  read_textbook: { emoji: '📖', color: '#22c55e', bg: '#d1fae5', label: 'کتاب' },
  do_exercise: { emoji: '✏️', color: '#f59e0b', bg: '#fef3c7', label: 'تمرین' },
  explore: { emoji: '🧭', color: '#14b8a6', bg: '#ccfbf1', label: 'کاوش' },
  exam: { emoji: '📝', color: '#ec4899', bg: '#fce7f3', label: 'آزمون' },
  challenge: { emoji: '⚔️', color: '#f59e0b', bg: '#fef3c7', label: 'چالش' },
  follow_plan: { emoji: '📅', color: '#8b5cf6', bg: '#f3e8ff', label: 'برنامه' },
  create_plan: { emoji: '🗓️', color: '#8b5cf6', bg: '#f3e8ff', label: 'برنامه' },
  streak_check: { emoji: '🔥', color: '#f97316', bg: '#ffedd5', label: 'استریک' },
  daily_spin: { emoji: '🎡', color: '#ef4444', bg: '#fee2e2', label: 'گردونه' },
  view_medals: { emoji: '🏅', color: '#d97706', bg: '#fef3c7', label: 'مدال' },
  celebrate: { emoji: '🎉', color: '#ec4899', bg: '#fce7f3', label: 'جشن' },
  almost_medal: { emoji: '⭐', color: '#d97706', bg: '#fef3c7', label: 'مدال' },
  leaderboard: { emoji: '🏆', color: '#d97706', bg: '#fef3c7', label: 'رتبه' },
  challenge_friend: { emoji: '🤝', color: '#3b82f6', bg: '#dbeafe', label: 'چالش' },
  join_group: { emoji: '👥', color: '#06b6d4', bg: '#cffafe', label: 'گروه' },
  take_break: { emoji: '☕', color: '#ec4899', bg: '#fce7f3', label: 'استراحت' },
  sleep_reminder: { emoji: '🌙', color: '#6366f1', bg: '#e0e7ff', label: 'خواب' },
  mood_check: { emoji: '😊', color: '#22c55e', bg: '#d1fae5', label: 'احوال' },
  learn_skill: { emoji: '🧠', color: '#8b5cf6', bg: '#f3e8ff', label: 'مهارت' },
  parent_view: { emoji: '👨‍👩‍👧', color: '#a78bfa', bg: '#ede9fe', label: 'والدین' },
  set_grade: { emoji: '🎓', color: '#06b6d4', bg: '#cffafe', label: 'پایه' },
  pick_first_book: { emoji: '📕', color: '#10b981', bg: '#d1fae5', label: 'شروع' },
  watch_first_video: { emoji: '▶️', color: '#ef4444', bg: '#fee2e2', label: 'ویدیو' },
  complete_first_lesson: { emoji: '🏁', color: '#22c55e', bg: '#d1fae5', label: 'درس' },
  take_first_quiz: { emoji: '📋', color: '#ec4899', bg: '#fce7f3', label: 'آزمون' },
  wait_correction: { emoji: '⏳', color: '#94a3b8', bg: '#f1f5f9', label: 'تصحیح' },
  fix_streak: { emoji: '🔥', color: '#f97316', bg: '#ffedd5', label: 'استریک' },
  resume_yesterday: { emoji: '▶️', color: '#10b981', bg: '#d1fae5', label: 'ادامه' },
};
const getMeta = (type) => MISSION_META[type] || { emoji: '🎯', color: '#8b5cf6', bg: '#f3e8ff', label: 'پیشنهاد' };

const SmartCoachModal = ({
  isOpen,
  todayPlan,
  quota = { freeUsed: 0, paidCount: 0, totalCoinsSpent: 0 },
  stats,
  progress = {},
  isPaying,
  isGenerating,
  regenCost = 100,
  rewardCoins = 0,
  rewardClaimed = false,
  onClose,
  onStartMission,
  onPayAndGenerate,
}) => {
  const { user } = useAuth();

  const [view, setView] = useState('thinking');
  const [rendered, setRendered] = useState(false);
  const [openClass, setOpenClass] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const [localProgress, setLocalProgress] = useState({});
  const [pendingIds, setPendingIds] = useState({});

  const headSubRef = useRef(null);
  const progressFillRef = useRef(null);
  const progressPctRef = useRef(null);
  const progressLabelRef = useRef(null);
  const stageRefs = useRef([]);
  const chartRefs = useRef([]);
  const num1Ref = useRef(null);
  const num2Ref = useRef(null);
  const num3Ref = useRef(null);
  const timeoutsRef = useRef([]);
  const cancelledRef = useRef(false);

  const clearAll = useCallback(() => {
    cancelledRef.current = true;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const T = useCallback((fn, delay) => {
    const t = setTimeout(() => { if (!cancelledRef.current) fn(); }, delay);
    timeoutsRef.current.push(t);
    return t;
  }, []);

  // mount/unmount
  useEffect(() => {
    if (isOpen) {
      setRendered(true);
      const raf = requestAnimationFrame(() => setOpenClass(true));
      return () => cancelAnimationFrame(raf);
    } else if (rendered) {
      setOpenClass(false);
      const t = setTimeout(() => setRendered(false), 320);
      return () => clearTimeout(t);
    }
  }, [isOpen, rendered]);

  // body lock
  useEffect(() => {
    if (!rendered) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [rendered]);

  // progress refresh
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    try { setLocalProgress(getStepProgress(user.id) || {}); } catch {}
    try {
      const pending = readJSON(`aiCoach_pending_${user.id}`, {});
      const ids = {};
      Object.keys(pending).forEach((id) => { ids[id] = true; });
      setPendingIds(ids);
    } catch {}
  }, [isOpen, user?.id]);

  // helpers
  const animateCounter = useCallback((el, target, duration) => {
    if (!el) return;
    const start = performance.now();
    const step = (now) => {
      if (cancelledRef.current) return;
      const elapsed = now - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = p < 0.4 ? Math.floor(Math.random() * target * 0.9) : Math.floor(target * eased);
      el.textContent = toFa(v);
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = toFa(target);
    };
    requestAnimationFrame(step);
  }, []);

  const setProgressPct = useCallback((pct) => {
    if (progressFillRef.current) progressFillRef.current.style.width = pct + '%';
    if (progressPctRef.current) progressPctRef.current.textContent = toFa(Math.round(pct)) + '٪';
  }, []);

  const showStage = useCallback((idx) => {
    chartRefs.current.forEach((s, i) => s && s.classList.toggle('active', i === idx));
  }, []);

  const markStageDone = useCallback((idx) => {
    if (stageRefs.current[idx]) stageRefs.current[idx].classList.add('done');
  }, []);

  // open behavior
  useEffect(() => {
    if (!isOpen) { clearAll(); setView('thinking'); return; }
    cancelledRef.current = false;
    setFadingOut(false);

    // پلن موجود، تولید نمی‌شود → سریع به content
if (todayPlan && !isGenerating) {
  setView('content');
  return () => clearAll();
}

    // پلن موجود نیست + تولید نیست → paywall
    if (!todayPlan && !isGenerating) {
      setView('thinking');
      T(() => setView('paywall'), 400);
      return () => clearAll();
    }

    // در حال تولید → انیمیشن ۷ ثانیه
    setView('thinking');

    if (headSubRef.current) headSubRef.current.textContent = 'اسکن اطلاعات اولیه...';
    if (progressLabelRef.current) progressLabelRef.current.textContent = 'خواندن داده‌ها...';
    if (progressFillRef.current) progressFillRef.current.style.width = '0%';
    if (progressPctRef.current) progressPctRef.current.textContent = '۰٪';
    if (num1Ref.current) num1Ref.current.textContent = '0';
    if (num2Ref.current) num2Ref.current.textContent = '0';
    if (num3Ref.current) num3Ref.current.textContent = '0';
    stageRefs.current.forEach((s) => s && s.classList.remove('done'));
    chartRefs.current.forEach((s, i) => s && s.classList.toggle('active', i === 0));

    animateCounter(num1Ref.current, 12, 2200);
    animateCounter(num2Ref.current, 34, 2400);
    animateCounter(num3Ref.current, 245, 2600);
    setProgressPct(8);

    T(() => { setProgressPct(25); if (headSubRef.current) headSubRef.current.textContent = 'خواندن نمرات...'; }, 400);
    T(() => setProgressPct(35), 900);
    T(() => markStageDone(0), 1600);
    T(() => {
      if (progressLabelRef.current) progressLabelRef.current.textContent = 'تحلیل نمرات...';
      if (headSubRef.current) headSubRef.current.textContent = 'تحلیل آزمون‌ها...';
      showStage(1);
      setProgressPct(42);
    }, 2300);
    T(() => setProgressPct(55), 2900);
    T(() => { if (headSubRef.current) headSubRef.current.textContent = 'بررسی روند مطالعه...'; }, 3300);
    T(() => setProgressPct(62), 3700);
    T(() => markStageDone(1), 4200);
    T(() => {
      if (progressLabelRef.current) progressLabelRef.current.textContent = 'ساخت برنامه...';
      if (headSubRef.current) headSubRef.current.textContent = 'ساخت ماموریت‌های اختصاصی...';
      showStage(2);
      setProgressPct(70);
    }, 4600);
    T(() => setProgressPct(82), 5200);
    T(() => setProgressPct(90), 5800);
    T(() => {
      if (progressLabelRef.current) progressLabelRef.current.textContent = 'تقریباً آماده‌ست...';
      if (headSubRef.current) headSubRef.current.textContent = 'نهایی‌سازی...';
      setProgressPct(96);
    }, 6300);
    T(() => markStageDone(2), 6600);
    T(() => setProgressPct(100), 6800);

    T(() => {
      setFadingOut(true);
      T(() => { setView('content'); setFadingOut(false); }, 450);
    }, 7000);

    return () => clearAll();
  }, [isOpen]); // eslint-disable-line

  // fallback: اگر پلن پس از thinking رسید
  useEffect(() => {
    if (!isOpen || view !== 'thinking' || isGenerating || !todayPlan) return;
    if (!fadingOut) T(() => setView('content'), 300);
  }, [isOpen, view, isGenerating, todayPlan, fadingOut, T]);

  // Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isPaying) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isPaying, onClose]);

  const handleRegenerate = () => setView('paywall');
  const handleCancelPaywall = () => setView('content');
  const handlePay = async () => {
    try {
      await onPayAndGenerate();
      setView('content');
    } catch {}
  };
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isPaying) onClose();
  };

  const suggestions = safeArray(todayPlan?.suggestions);
  const mergedProgress = { ...localProgress, ...progress };
  const doneCount = suggestions.filter((s) => mergedProgress[s.id]).length;
  const totalCount = suggestions.length;
  const allDone = totalCount > 0 && doneCount === totalCount;

  const weekly = stats?.weekly || [0, 0, 0, 0, 0, 0, 0];
  const maxW = Math.max(...weekly, 1);
  const barH = weekly.map((v) => Math.max(5, Math.round((v / maxW) * 100)));
  const dayLabels = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
  const level = stats?.level ?? 1;
  const xpInLevel = stats?.xpInLevel ?? 0;
  const starsCount = stats?.stars ?? 1;
  const growth = stats?.growth ?? 0;

  if (!rendered) return null;

  return (
    <div className={`smartcoachmodal overlay ${openClass ? 'open' : ''}`} onClick={handleOverlayClick} dir="rtl">
      <div className="modal" role="dialog" aria-modal="true">

        {/* HEADER */}
        <div className="modal-head">
          <i className="fas fa-star head-spark s1" />
          <i className="fas fa-star head-spark s2" />
          <i className="fas fa-star head-spark s3" />

          <div
            className="head-thinking"
            style={{
              display: view === 'thinking' ? 'flex' : 'none',
              opacity: fadingOut ? 0 : 1,
              transition: fadingOut ? 'opacity 0.4s ease' : 'none',
            }}
          >
            <div className="ai-avatar-wrap">
              <div className="ai-avatar-glow" />
              <div className="ai-avatar-ring" />
              <div className="ai-avatar-ring r2" />
              <div className="ai-avatar-ring r3" />
              <div className="ai-avatar">🤖</div>
            </div>
            <div className="think-status">
              <div className="think-title">
                در حال تحلیل
                <span className="dot"><span /><span /><span /></span>
              </div>
              <div className="think-sub" ref={headSubRef}>اسکن اطلاعات اولیه...</div>
            </div>
          </div>

          <div
            className={`head-content ${view === 'content' ? 'show' : ''}`}
            style={{ display: view === 'content' ? 'flex' : 'none' }}
          >
            <div className="mascot">🦊</div>
            <div className="head-text">
              <div className="head-title">ماجراجویی امروز</div>
              <div className="head-sub">
                سلام {user?.firstName || user?.name || 'دوست من'}! بریم که بترکونیم 💪
              </div>
            </div>
          </div>

          <div
            className="head-content"
            style={{ display: view === 'paywall' ? 'flex' : 'none', opacity: 1 }}
          >
            <div className="mascot">💰</div>
            <div className="head-text">
              <div className="head-title">دریافت پیشنهاد جدید</div>
              <div className="head-sub">{toFa(regenCost)} سکه از موجودی شما کم می‌شود</div>
            </div>
          </div>

          <button className="close-btn" onClick={onClose} disabled={isPaying} aria-label="بستن">
            <i className="fas fa-xmark" />
          </button>
        </div>

        {/* BODY */}
        <div className="modal-body">

          {/* THINKING */}
          <div
            className={`thinking-phase ${fadingOut ? 'fade-out' : ''}`}
            style={{ display: view === 'thinking' ? 'block' : 'none' }}
          >
            <div className="grid-overlay" />
            <div className="scan-line" />

            <div className="think-viz">
              <div className="chart-stage active" ref={(el) => (chartRefs.current[0] = el)}>
                <svg viewBox="0 0 280 100">
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                  <rect className="bar b1" x="10" y="60" width="28" height="40" rx="4" fill="url(#barGrad)" />
                  <rect className="bar b2" x="48" y="40" width="28" height="60" rx="4" fill="url(#barGrad)" />
                  <rect className="bar b3" x="86" y="52" width="28" height="48" rx="4" fill="url(#barGrad)" />
                  <rect className="bar b4" x="124" y="25" width="28" height="75" rx="4" fill="url(#barGrad)" />
                  <rect className="bar b5" x="162" y="35" width="28" height="65" rx="4" fill="url(#barGrad)" />
                  <rect className="bar b6" x="200" y="15" width="28" height="85" rx="4" fill="url(#barGrad)" />
                  <rect className="bar b7" x="238" y="5" width="28" height="95" rx="4" fill="url(#barGrad)" />
                </svg>
                <span className="chart-label">📊 تحلیل هفتگی</span>
              </div>

              <div className="chart-stage" ref={(el) => (chartRefs.current[1] = el)}>
                <svg viewBox="0 0 280 100">
                  <defs>
                    <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#22d3ee" />
                      <stop offset="100%" stopColor="#ec4899" />
                    </linearGradient>
                  </defs>
                  <path
                    className="line-path"
                    d="M 10 78 Q 40 65, 60 68 T 110 55 T 160 40 T 210 25 T 270 12"
                    fill="none"
                    stroke="url(#lineGrad)"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <circle cx="270" cy="12" r="5" fill="#ec4899" />
                </svg>
                <span className="chart-label label-2">📈 روند نمرات</span>
              </div>

              <div className="chart-stage" ref={(el) => (chartRefs.current[2] = el)}>
                <svg viewBox="0 0 280 120">
                  <defs>
                    <linearGradient id="radarGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.4" />
                    </linearGradient>
                  </defs>
                  <g className="radar-grid" transform="translate(140,60)">
                    <circle r="15" /><circle r="30" /><circle r="45" />
                    <line className="radar-axis" x1="0" y1="-45" x2="0" y2="45" />
                    <line className="radar-axis" x1="-43" y1="-15" x2="43" y2="15" />
                    <line className="radar-axis" x1="-43" y1="15" x2="43" y2="-15" />
                  </g>
                  <g transform="translate(140,60)">
                    <polygon
                      className="radar-shape"
                      points="0,-38 32,-12 28,28 -28,28 -32,-12"
                      fill="url(#radarGrad)"
                      stroke="#22d3ee"
                      strokeWidth="2"
                    />
                  </g>
                </svg>
                <span className="chart-label label-3">🎯 تحلیل جامع</span>
              </div>
            </div>

            <div className="think-stats">
              <div className="think-stat">
                <div className="think-stat-icon"><i className="fas fa-book" /></div>
                <div className="think-stat-num" ref={num1Ref}>0</div>
                <div className="think-stat-lbl">درس بررسی شد</div>
              </div>
              <div className="think-stat">
                <div className="think-stat-icon"><i className="fas fa-clipboard-check" /></div>
                <div className="think-stat-num" ref={num2Ref}>0</div>
                <div className="think-stat-lbl">آزمون تحلیل شد</div>
              </div>
              <div className="think-stat">
                <div className="think-stat-icon"><i className="fas fa-clock" /></div>
                <div className="think-stat-num" ref={num3Ref}>0</div>
                <div className="think-stat-lbl">دقیقه مطالعه</div>
              </div>
            </div>

            <div className="think-progress-wrap">
              <div className="think-progress-label">
                <span className="label" ref={progressLabelRef}>خواندن داده‌ها...</span>
                <span className="pct" ref={progressPctRef}>۰٪</span>
              </div>
              <div className="think-progress">
                <div className="think-progress-fill" ref={progressFillRef} />
              </div>
              <div className="think-stages">
                <span ref={(el) => (stageRefs.current[0] = el)}>خواندن</span>
                <span ref={(el) => (stageRefs.current[1] = el)}>تحلیل</span>
                <span ref={(el) => (stageRefs.current[2] = el)}>ساخت برنامه</span>
              </div>
            </div>
          </div>

          {/* CONTENT */}
          <div
            className={`content-phase ${view === 'content' ? 'show' : ''}`}
            style={{ display: view === 'content' ? 'flex' : 'none' }}
          >
            <div className="card level-card">
              <div className="level-badge">{toFa(level)}</div>
              <div className="level-content">
                <div className="level-row">
                  <span className="name">سطح {toFa(level)} · کاوشگر</span>
                  <span className="xp-label"><b>{toFa(xpInLevel)}</b> / ۱۰۰ XP</span>
                </div>
                <div className="level-bar">
                  <div className="level-fill" style={{ width: `${xpInLevel}%` }} />
                </div>
                <div className="level-stars">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span key={i} className={i <= starsCount ? 'on' : ''}>⭐</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="card chart-card">
              <div className="chart-head">
                <div className="chart-title"><span className="emoji">📊</span> وضعیت هفته‌ات</div>
                <span className="chart-badge">
                  {growth >= 0 ? '📈' : '📉'} {growth >= 0 ? '+' : ''}{toFa(growth)}٪
                </span>
              </div>
              <div className="chart-bars">
                {barH.map((h, i) => (
                  <div key={i} className={`bar-col ${i === 6 ? 'today' : ''}`}>
                    <div className="bar-track"><div className="bar-fill" style={{ height: `${h}%` }} /></div>
                    <div className="bar-label">{dayLabels[i]}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card insights-card">
              <div className="insights-head"><span>✨</span> یه نگاه سریع</div>
              <div className="insights-grid">
                <div className="insight-item">
                  <div className="insight-icon fire"><i className="fas fa-fire" /></div>
                  <div className="insight-body">
                    <div className="insight-value">{toFa(stats?.streak || 0)} روز</div>
                    <div className="insight-label">استریک فعال</div>
                  </div>
                </div>
                <div className="insight-item">
                  <div className="insight-icon trend"><i className="fas fa-arrow-trend-up" /></div>
                  <div className="insight-body">
                    <div className="insight-value">{growth >= 0 ? '+' : ''}{toFa(growth)}٪</div>
                    <div className="insight-label">رشد این هفته</div>
                  </div>
                </div>
                <div className="insight-item">
                  <div className="insight-icon target"><i className="fas fa-bullseye" /></div>
                  <div className="insight-body">
                    <div className="insight-value">{stats?.examCount > 0 ? `${toFa(stats.examAvg)}٪` : '—'}</div>
                    <div className="insight-label">میانگین آزمون</div>
                  </div>
                </div>
                <div className="insight-item">
                  <div className="insight-icon star"><i className="fas fa-star" /></div>
                  <div className="insight-body">
                    <div className="insight-value">{toFa(stats?.almostMedals || 0)} مدال</div>
                    <div className="insight-label">نزدیک باز شدن</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="section-header">
              <div className="section-title"><span className="emoji">🎯</span> ماموریت‌های امروز</div>
              <span className="section-badge">
                {totalCount > 0 ? `${toFa(doneCount)} از ${toFa(totalCount)} انجام شد` : 'آماده'}
              </span>
            </div>

            <div className="missions">
              {suggestions.length === 0 && (
                <div className="mission" style={{ '--mc': '#94a3b8', '--mb': '#f1f5f9' }}>
                  <div className="mission-emoji">✨</div>
                  <div className="mission-body">
                    <div className="mission-title">همه‌چیز عالیه!</div>
                    <div className="mission-meta">
                      <span><i className="fas fa-check" /> فردا با ماموریت تازه برمی‌گردم</span>
                    </div>
                  </div>
                </div>
              )}

              {suggestions.map((sug) => {
                const meta = getMeta(sug.type);
                const isDone = !!mergedProgress[sug.id];
                const isPending = !!pendingIds[sug.id] && !isDone;
                const isUrgent = (sug.urgency || 0) >= 7;

                return (
                  <div
                    key={sug.id}
                    className={`mission ${isUrgent ? 'urgent' : ''} ${isDone ? 'done' : ''}`}
                    style={{ '--mc': meta.color, '--mb': meta.bg }}
                  >
                    <div className="mission-emoji">{isDone ? '✅' : meta.emoji}</div>
                    <div className="mission-body">
                      <div className="mission-title">
                        {sug.title}
                        {isUrgent && !isDone && <span className="urgent-tag">فوری!</span>}
                      </div>
                      <div className="mission-meta">
                        <span><i className="fas fa-clock" /> {toFa(sug.estimatedDuration || 15)} دقیقه</span>
                        <span><i className="fas fa-tag" /> {meta.label}</span>
                      </div>
                    </div>
                    {isDone ? (
                      <div className="mission-state done"><i className="fas fa-check" /></div>
                    ) : isPending ? (
                      <div className="mission-state loading"><i className="fas fa-spinner fa-spin" /></div>
                    ) : (
                      <button className="mission-action" onClick={() => onStartMission(sug)}>بریم</button>
                    )}
                  </div>
                );
              })}
            </div>

<div className="reward-card">
  <div className="reward-emoji">{rewardClaimed || allDone ? '🏆' : '🎁'}</div>
  <div className="reward-body">
    <div className="reward-title">
      {rewardClaimed
        ? `🎉 ${toFa(rewardCoins)} سکه گرفتی!`
        : allDone
          ? '🎉 همه‌ی ماموریت‌ها تموم شد!'
          : '🎁 جایزه‌ی امروز'}
    </div>
    <div className="reward-text">
      {rewardClaimed
        ? 'پاداش امروز به حسابت واریز شد — فردا دوباره با ماموریت‌های تازه!'
        : allDone
          ? 'در حال واریز پاداش...'
          : rewardCoins > 0
            ? `همه‌ی ماموریت‌های امروز رو تموم کن تا ${toFa(rewardCoins)} سکه بگیری!`
            : 'همه‌ی ماموریت‌های امروز رو تموم کن!'}
    </div>
  </div>
  <div className="reward-medal">{rewardCoins > 0 ? '🪙' : '🎖️'}</div>
</div>

            <button
              className="coach-regen-btn"
              onClick={handleRegenerate}
              disabled={isPaying}
            >
              {isPaying ? '⏳ در حال ساخت...' : `🔄 دریافت پیشنهاد جدید (${toFa(regenCost)} سکه)`}
            </button>
          </div>

          {/* PAYWALL */}
          <div
            className="paywall-phase"
            style={{ display: view === 'paywall' ? 'flex' : 'none' }}
          >
            <div className="paywall-icon">💰</div>
            <div className="paywall-title">
              {todayPlan ? 'پیشنهاد امروزت رو ساختی!' : 'سهمیه‌ی امروزت تمام شده'}
            </div>
<div className="paywall-text">
  امروز یک بار از مشاور هوشمند استفاده کردی. برای دریافت پیشنهاد تازه، {toFa(regenCost)} سکه لازمه.
</div>
            <button
              className="paywall-btn"
              onClick={handlePay}
              disabled={isPaying}
            >
              {isPaying ? '⏳ در حال ساخت...' : `💎 دریافت با ${toFa(regenCost)} سکه`}
            </button>
            {todayPlan && (
              <button
                className="paywall-cancel"
                onClick={handleCancelPaywall}
                disabled={isPaying}
              >
                انصراف
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartCoachModal;